import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getCurrentOrderRedirectPayment, getPaymentMethodsForOrder, isEmptyArray, isNonEmptyArray, loadCurrentOrderRedirectPayment, LoadingState, loadOrderCompletion, loadPaymentMethodsForOrder, logger } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { PaymentFailedError, PaymentFailedErrorResultCode } from '@vista-digital/embedded-payments';
import { OrderCompletionStatus, OrderPaymentStatus } from '@vista-digital/ocapi-types/v1';
import { useMountEffect } from '@vista-digital/react-tools';
import { observer } from 'mobx-react';
import { useContext, useEffect, useState } from 'react';
import { Button, ButtonColor, ButtonSize } from '../../../subcomponents/button';
import { LoadingIndicator } from '../../../subcomponents/loading-indicator/loading-indicator';
import { LoadingIndicatorType } from '../../../subcomponents/loading-indicator/types/type';
import { OrderPaymentFormContextInstance } from '../types/context';
import { getEmbeddedPaymentMethods, getRedirectPaymentMethods, getSortedPaymentMethods } from '../utils/payment-methods';
import { hasRedirectTokenUrlParameter, removeRedirectTokenUrlParamter } from '../utils/redirect-token-url-parameter';
import { OrderPaymentFormEmbeddedPaymentMethods } from './embedded-payment-methods';
import { OrderPaymentFormMessage, OrderPaymentFormMessageType } from './message';
import { OrderPaymentFormRedirectPaymentMethods } from './redirect-payment-methods';
const b = bemClassBuilder('v-order-payment-form-methods');
export const OrderPaymentFormMethods = observer(props => {
    const { order } = props;
    const { assets, displayText, onPaymentSuccessful, onPaymentFailed } = useContext(OrderPaymentFormContextInstance);
    const [loadingState, setLoadingState] = useState(LoadingState.Loading);
    const [messageType, setMessageType] = useState();
    const [loadingMessage, setLoadingMessage] = useState(undefined);
    const paymentMethods = getPaymentMethodsForOrder(order);
    const sortedPaymentMethods = getSortedPaymentMethods(paymentMethods);
    const embeddedPaymentMethods = getEmbeddedPaymentMethods(sortedPaymentMethods);
    const redirectPaymentMethods = getRedirectPaymentMethods(sortedPaymentMethods);
    const currentRedirectPayment = getCurrentOrderRedirectPayment();
    const redirectPaymentStarted = hasRedirectTokenUrlParameter();
    useMountEffect(() => {
        (async () => {
            try {
                setLoadingState(LoadingState.Loading);
                await Promise.all([
                    loadPaymentMethodsForOrder(order),
                    // If we've attempted redirect payment (should have "token" URL parameter from WPM), poll. else don't poll.
                    // This is eagerly loaded here instead of [[OrderPaymentFormRedirectPaymentMethods]] to improve performance.
                    loadCurrentOrderRedirectPayment(redirectPaymentStarted)
                ]);
                setLoadingState(LoadingState.Success);
            }
            catch (error) {
                logger.error('Unable to load payment data.', error);
                setLoadingState(LoadingState.Failure);
            }
        })();
    });
    useEffect(() => {
        if (paymentMethods && isEmptyArray(paymentMethods)) {
            logger.error('Unable to initialise Order Payment Form. No payment methods are configured for the current order.');
            setMessageType(OrderPaymentFormMessageType.FailedToLoadData);
        }
    }, [paymentMethods]);
    useEffect(() => {
        (async () => {
            // No need to do anything if redirect payment hasn't started or is still loading.
            if (!redirectPaymentStarted || !currentRedirectPayment)
                return;
            if (currentRedirectPayment.status !== OrderPaymentStatus.Completed) {
                setMessageType(OrderPaymentFormMessageType.PaymentDeclined);
                return onPaymentFailed(new PaymentFailedError(PaymentFailedErrorResultCode.PaymentDeclined));
            }
            setLoadingMessage(displayText.processingPaymentMessage);
            const orderCompletion = await loadOrderCompletion();
            if (!orderCompletion || orderCompletion.status !== OrderCompletionStatus.Completed) {
                setMessageType(OrderPaymentFormMessageType.OrderCompletionFailed);
                return onPaymentFailed(new PaymentFailedError(PaymentFailedErrorResultCode.OrderCompletionFailed));
            }
            onPaymentSuccessful(orderCompletion);
        })();
    }, [
        order.id,
        currentRedirectPayment,
        displayText.processingPaymentMessage,
        onPaymentFailed,
        onPaymentSuccessful,
        setMessageType,
        setLoadingMessage,
        redirectPaymentStarted
    ]);
    const handleTryAgainButtonClicked = () => {
        removeRedirectTokenUrlParamter();
        setMessageType(undefined);
    };
    if (messageType) {
        const hasPaymentDeclined = messageType === OrderPaymentFormMessageType.PaymentDeclined;
        if (hasPaymentDeclined) {
            return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx("div", Object.assign({ className: b.element('content-wrapper') }, { children: _jsx(OrderPaymentFormMessage, Object.assign({ messageType: messageType }, { children: _jsx(Button, { label: displayText.paymentDeclinedTryAgainButtonLabel, size: ButtonSize.Large, color: ButtonColor.Primary, onClick: handleTryAgainButtonClicked }) })) })) })));
        }
        return _jsx(OrderPaymentFormMessage, { messageType: messageType });
    }
    if (loadingState === LoadingState.Failure) {
        return _jsx(OrderPaymentFormMessage, { messageType: OrderPaymentFormMessageType.FailedToLoadData });
    }
    if (!paymentMethods || loadingState === LoadingState.Loading || loadingMessage) {
        return (_jsx("div", Object.assign({ className: b.block() }, { children: _jsx("div", Object.assign({ className: b.element('content-wrapper') }, { children: _jsx(LoadingIndicator, { iconName: assets.processingPaymentIconName, message: loadingMessage, type: LoadingIndicatorType.Spinner }) })) })));
    }
    return (_jsxs("div", Object.assign({ className: b.block() }, { children: [sortedPaymentMethods.length > 1 && (_jsx("h3", Object.assign({ className: b.element('heading') }, { children: displayText.paymentMethodsHeading }))), _jsx(OrderPaymentFormEmbeddedPaymentMethods, { paymentMethods: embeddedPaymentMethods }), isNonEmptyArray(embeddedPaymentMethods) && isNonEmptyArray(redirectPaymentMethods) && (_jsx("hr", { className: b.element('separator') })), _jsx(OrderPaymentFormRedirectPaymentMethods, { paymentMethods: redirectPaymentMethods, setError: setMessageType })] })));
});
OrderPaymentFormMethods.displayName = 'OrderPaymentFormMethods';
